import { Popover as BasePopover } from "@base-ui/react/popover";
import classNames from "classnames";
import * as React from "react";

import styles from "./Popover.module.css";

type BaseUIClassName<State> =
  | string
  | ((state: State) => string | undefined)
  | undefined;

/**
 * Merges a house class with a consumer's. Base UI's `className` is either a
 * string or a function of the part's state, so both forms have to survive.
 */
function withHouseClass<State>(
  houseClass: string,
  className: BaseUIClassName<State>,
): BaseUIClassName<State> {
  return typeof className === "function"
    ? (state: State) => classNames(houseClass, className(state))
    : classNames(houseClass, className);
}

export interface PopoverRootProps extends BasePopover.Root.Props {}

function PopoverRoot(props: PopoverRootProps) {
  return <BasePopover.Root {...props} />;
}

export interface PopoverTriggerProps extends BasePopover.Trigger.Props {}

/**
 * The control that opens the popup. Hover and delay props live here rather
 * than on the root, as they do in Base UI.
 */
const Trigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  function PopoverTriggerPart(props, ref) {
    return <BasePopover.Trigger {...props} ref={ref} />;
  },
);

export interface PopoverContentProps
  extends BasePopover.Popup.Props,
    Pick<BasePopover.Positioner.Props, "side" | "align"> {}

/**
 * The floating surface, fusing Base UI's Portal, Positioner, and Popup.
 *
 * This is the one deliberately curated part: `Popup` props pass through
 * (`initialFocus`, `finalFocus`, `render`, `aria-label`, …), the positioner is
 * limited to `side` and `align` because the offsets and z-index are system
 * decisions, and Portal props are not exposed at all.
 *
 * Size the popup via `className` — width, or a max-height cap through the
 * `--popover-max-height` custom property (it never exceeds the viewport
 * regardless).
 *
 * The popup clips its content, which is what keeps long content inside the
 * height cap and the rounded corners. A body that renders its own floating
 * layer has to portal that layer out rather than paint past the popup edge:
 * Base UI positions the popup with a `transform`, which also makes it the
 * containing block for any `position: fixed` descendant.
 */
const Content = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContentPart(
    { side = "bottom", align = "start", className, children, ...popupProps },
    ref,
  ) {
    return (
      <BasePopover.Portal>
        {/* 8 = --spacing-xs; JS props can't read CSS variables. */}
        <BasePopover.Positioner
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={8}
          className={styles.positioner}
        >
          <BasePopover.Popup
            {...popupProps}
            ref={ref}
            className={withHouseClass(styles.popup, className)}
          >
            <BasePopover.Arrow className={styles.arrow}>
              <ArrowSvg />
            </BasePopover.Arrow>
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    );
  },
);

export interface PopoverTitleProps extends BasePopover.Title.Props {}

/** A visible heading that names the popup, in place of an `aria-label`. */
const Title = React.forwardRef<HTMLHeadingElement, PopoverTitleProps>(
  function PopoverTitlePart({ className, ...props }, ref) {
    return (
      <BasePopover.Title
        {...props}
        ref={ref}
        className={withHouseClass(styles.title, className)}
      />
    );
  },
);

export interface PopoverDescriptionProps
  extends BasePopover.Description.Props {}

/** Secondary body text under the `Title`, describing the popup. */
const Description = React.forwardRef<
  HTMLParagraphElement,
  PopoverDescriptionProps
>(function PopoverDescriptionPart({ className, ...props }, ref) {
  return (
    <BasePopover.Description
      {...props}
      ref={ref}
      className={withHouseClass(styles.description, className)}
    />
  );
});

export interface PopoverCloseProps extends BasePopover.Close.Props {}

/** A button inside the popup that dismisses it. */
const Close = React.forwardRef<HTMLButtonElement, PopoverCloseProps>(
  function PopoverClosePart(props, ref) {
    return <BasePopover.Close {...props} ref={ref} />;
  },
);

/**
 * The tail, drawn as paths rather than a CSS shape so the border can trace the
 * two outer edges only.
 */
function ArrowSvg() {
  return (
    <svg
      className={styles.arrowSvg}
      width="20"
      height="10"
      viewBox="0 0 20 10"
      fill="none"
      aria-hidden="true"
    >
      <path
        className={styles.arrowFill}
        d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
      />
      <path
        className={styles.arrowStroke}
        d="M10.3333 3.34539L5.47654 7.71648C4.55842 8.54279 3.36693 9 2.13172 9H0V8H2.13172C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8H20V9H18.5349C17.2998 9 16.1083 8.54278 15.1901 7.71648L10.3333 3.34539Z"
      />
    </svg>
  );
}

/**
 * A floating panel anchored to a trigger element
 *
 * Focus moves to the first tabbable element in the popup on open and returns
 * to the trigger on close. Escape and outside-click dismiss.
 *
 * @example
 * ```tsx
 * <Popover>
 *   <Popover.Trigger render={<RobotoButton variant="secondary">Columns</RobotoButton>} />
 *   <Popover.Content aria-label="Choose columns">
 *     <PickerBody />
 *   </Popover.Content>
 * </Popover>
 * ```
 */
export const Popover = Object.assign(PopoverRoot, {
  Trigger,
  Content,
  Title,
  Description,
  Close,
});
